import { COUNTRY_CODE } from '@/utils/constants';
import {
  GENERATION,
  TERMINAL_NAME_NO_CONNECTION,
  COMMISSION_RESULT_VERDICT,
  SOLSHARE_VERSION,
  COMMISSION_RESULT_FAIL_REASON,
} from './constants';
import {
  COMPONENT_ERROR_TYPES,
  MK2_ONLY_ERROR_TYPES,
  type ComponentCountryCode,
  type ComponentGeneration,
  type ComponentErrorType,
  type ComponentSolshareVersion,
  DEFAULT_ERROR_COMPONENT,
  COMMISSION_RESULT_CONFIGS,
} from './error-constants';
import type { TConnectionGroup } from '../models/Connection';
import type { Terminal, CommissionResult } from '../models/CommissioningDevice';
import { getSolshareVersion, getMk1Generation } from '@/shared/utils/solshare-serial';

/**
 * Utility functions to resolve error component paths
 * Error component paths are resolved based on error code, device serial number, and country code.
 * The error pages are located in the error-pages directory and are named like:
 *
 * {ComponentSolshareVersion}/{ComponentGeneration}?/{ComponentErrorType}.{ComponentCountryCode}.vue
 *
 * For example:
 * ./Mk1/G1/InverterVoltageDetected.AU.vue
 * ./Mk1/G2/InverterVoltageDetected.AU.vue
 * ./Mk2/InverterVoltageDetected.AU.vue
 *
 * The ComponentSolshareVersion is derived from the serial number of the device (Mk1 or Mk2).
 * The ComponentGeneration is derived from the serial number of an Mk1 device.
 * The ComponentErrorType is derived from the commission result fail reason.
 * The ComponentCountryCode is derived from the project address country.
 *
 * These error pages are dynamically loaded using Vue's defineAsyncComponent at runtime.
 * The Troubleshoot.vue component is where the error page is displayed.
 *
 * ------------------------------------------------------------------------------------------------
 * Main utility functions are:
 * - getErrorComponentPath: Resolves the error troubleshooting page path from a given serial number, fail reason, and country code.
 * - getTerminalGroupingWithErrorsFromCommissionResult: Gets the terminal grouping with errors from the commission result
 *
 * ------------------------------------------------------------------------------------------------
 * These 4 functions are used to construct the error troubleshooting page path / filename.
 * NOTE: These are exported for testing purposes, thus MUST not be used in the application code.
 * ------------------------------------------------------------------------------------------------
 * - getComponentRouteSolshareVersionCode: Returns the folder name where the Mk1 or Mk2 error pages are located
 * - getComponentRouteGenerationCode: Returns the folder name where the G1 or G2 error pages are located
 * - getComponentRouteCountryCode: Returns the country code to be used in the troubleshooting error filename
 * - getComponentErrorType: Returns the filename of the error troubleshooting page based on the commission result fail reason
 *
 * ------------------------------------------------------------------------------------------------
 * Other utility functions:
 * NOTE: These are exported for testing purposes, thus MUST not be used in the application code.
 * - getApartmentNameFromTerminal: Gets the apartment name from the terminal name
 *
 * ------------------------------------------------------------------------------------------------
 * Private utility functions:
 * - __getPathToErrorTroubleshootingPage: Resolves the component import path to the error troubleshooting page
 * - __getSolshareVersionAndGenerationCodeFromSerialNumber: Returns the solshare version and generation code from a given serial number
 * - __isComponentErrorType: Type guard to check if a string is a valid ComponentErrorType
 */

/**
 * Returns the folder name where the Mk1 or Mk2 error pages are located
 * Error page path:{ComponentSolshareVersion}/{ComponentGeneration}?/{ComponentErrorType}.{ComponentCountryCode}.vue
 *
 * @param solshareVersion case insensitive
 * @returns Either ComponentSolshareVersion or null if not found
 * @internal Exported for testing purposes only
 */
export function getComponentRouteSolshareVersionCode(
  solshareVersion: string | null = null,
): ComponentSolshareVersion | null {
  if ([SOLSHARE_VERSION.Mk1.toLowerCase()].includes(solshareVersion?.toLowerCase() ?? '')) {
    return 'Mk1';
  }
  if ([SOLSHARE_VERSION.Mk2.toLowerCase()].includes(solshareVersion?.toLowerCase() ?? '')) {
    return 'Mk2';
  }
  return null;
}

/**
 * Returns the folder name where the G1 or G2 error pages are located (for Mk1 SolShare)
 * Error page path:{ComponentSolshareVersion}/{ComponentGeneration}?/{ComponentErrorType}.{ComponentCountryCode}.vue
 *
 * @param gen - The generation string (case insensitive)
 * @returns The ComponentGeneration or null if no matching generation exists
 * @internal Exported for testing purposes only
 */
export function getComponentRouteGenerationCode(
  gen: string | null = null,
): ComponentGeneration | null {
  if ([GENERATION.ONE, 'g1'].includes(gen?.toLowerCase() ?? '')) {
    return 'G1';
  }
  if ([GENERATION.TWO, 'g2'].includes(gen?.toLowerCase() ?? '')) {
    return 'G2';
  }
  return null;
}

/**
 * Returns the country code to be used in the troubleshooting error filename
 * Error page path:{ComponentSolshareVersion}/{ComponentGeneration}?/{ComponentErrorType}.{ComponentCountryCode}.vue
 *
 * @param country - The country code string
 * @returns The ComponentCountryCode or 'AU' if no matching country code exists
 * @internal Exported for testing purposes only
 */
export function getComponentRouteCountryCode(
  country: string | undefined | null,
): ComponentCountryCode | null {
  if (!country) return 'AU';

  const toUpperCase = country.toUpperCase();
  if (
    toUpperCase === COUNTRY_CODE['United Kingdom'] ||
    toUpperCase === COUNTRY_CODE.Germany ||
    toUpperCase === 'UK'
  ) {
    // UK and Germany share the same error pages
    return 'UK';
  }

  // Default to AU content if no matching country code exists
  return 'AU';
}

/**
 * Returns the filename of the error troubleshooting page based on the commission result fail reason
 * Error page path:{ComponentSolshareVersion}/{ComponentGeneration}?/{ComponentErrorType}.{ComponentCountryCode}.vue
 *
 * @param failReason - The commission result fail reason string
 * @returns The ComponentErrorType or null if no matching error type exists
 */
export function getComponentErrorType(
  failReason: COMMISSION_RESULT_FAIL_REASON,
): ComponentErrorType | null {
  switch (failReason) {
    case COMMISSION_RESULT_FAIL_REASON.inverter_off_timeout:
      return 'InverterVoltageDetected';

    case COMMISSION_RESULT_FAIL_REASON.grid_voltage_invalid:
      return 'InputMissingVoltage';

    case COMMISSION_RESULT_FAIL_REASON.safe_state_timeout:
      return 'CriticalFailure';
    
    case COMMISSION_RESULT_FAIL_REASON.measurement_timeout:
    case COMMISSION_RESULT_FAIL_REASON.generic_timeout:
    case COMMISSION_RESULT_FAIL_REASON.grid_unstable_start:
    case COMMISSION_RESULT_FAIL_REASON.grid_unstable_end:
    case COMMISSION_RESULT_FAIL_REASON.grid_unstable_during:
      return 'UnstableConditions';

    case COMMISSION_RESULT_FAIL_REASON.solar_unexpected_start:
    case COMMISSION_RESULT_FAIL_REASON.solar_unexpected_end:
      return 'UnexpectedSolarCurrent';

    case COMMISSION_RESULT_FAIL_REASON.ct_swapped:
    case COMMISSION_RESULT_FAIL_REASON.current_diff_unexpected:
      return 'CTReadingIncorrect';

    case COMMISSION_RESULT_FAIL_REASON.solar_observed_bad:
      return 'TestCurrentTooSmall';

    case COMMISSION_RESULT_FAIL_REASON.ct_reversed:
      return 'CTNegativeReversed';

    case COMMISSION_RESULT_FAIL_REASON.ct_missing:
      return 'CTMissing';
  }
  return null;
}

/**
 * Type guard to check if a string is a valid ComponentErrorType
 * @param value - The string to check
 * @returns True if the value is a valid ComponentErrorType
 */
function __isComponentErrorType(value: string | null): value is ComponentErrorType {
  return (COMPONENT_ERROR_TYPES as readonly string[]).includes(value ?? '');
}

/**
 * Gets the apartment name from the terminal name
 * @param connections - The connections is usually taken from the device specification store
 * @param terminal - The terminal name
 * @returns The apartment name or 'Unknown' if no apartment name is found
 * @internal Exported for testing purposes only
 */
export function getApartmentNameFromTerminal(
  connections: TConnectionGroup[],
  terminal: Terminal,
): string {
  const terminalDetails = terminal.split('-');
  const terminalNumber = terminalDetails[0] as 'L1' | 'L2' | 'L3';
  const groupNumber = terminalDetails[1] ? parseInt(terminalDetails[1]) : -1;

  const unknownApartmentName = 'Unknown';

  if (terminalDetails.length === 2) {
    const enteredApartmentName = (connections[groupNumber - 1][terminalNumber] ?? '').trim();
    return ![TERMINAL_NAME_NO_CONNECTION, ''].includes(enteredApartmentName)
      ? connections[groupNumber - 1][terminalNumber]
      : unknownApartmentName;
  }
  return unknownApartmentName;
}

/**
 * Returns an object which contains the unique errors from the commission result.
 * Each error type is an array of terminal and apartment names.
 *
 * @param results - The commission result
 * @param connections - These are the connections from the device specification store, which contains the terminal and apartment names.
 * @returns
 * An object which contains the unique errors from the commission result.
 * The keys are the ComponentErrorType and the values are arrays of terminal and apartment names.
 * @example
 * const grouping = getTerminalGroupingWithErrorsFromCommissionResult(commissionResult, connections);
 * returns {
 *   UnstableConditions: ['Apartment 1 (L1-1)', 'Apartment 3 (L2-3)', 'Apartment 7 (L3-3)'],
 *   InputMissingVoltage: ['Apartment 4 (L2-4)', 'Unknown (L1-3)'],
 * }
 */
export function getTerminalGroupingWithErrorsFromCommissionResult(
  results: CommissionResult,
  connections: TConnectionGroup[],
): Partial<Record<ComponentErrorType, string[]>> {
  const grouping: Partial<Record<ComponentErrorType, string[]>> = {};

  Object.keys(results).forEach((terminal) => {
    const result = results[terminal as Terminal];
    if (result.verdict === COMMISSION_RESULT_VERDICT.FAIL) {
      const errorType = getComponentErrorType(result.reason as COMMISSION_RESULT_FAIL_REASON);
      if (errorType) {
        const apartmentName = getApartmentNameFromTerminal(connections, terminal as Terminal);
        if (!grouping[errorType]) {
          grouping[errorType] = [];
        }
        grouping[errorType]!.push(`${apartmentName} (${terminal})`);
      }
    }
  });
  return grouping;
}

/**
 * Returns the accumulated config values from all the commission result fail reasons.
 * 
 * @param results - The commission result
 * @param configName - The config name
 * @returns An object which contains the unique values from the commission result and related config name.
 * The keys are the ComponentErrorType and the values are arrays of values.
 * @example
 * const grouping = getCommissionResultConfig(commissionResult, 'failId');
 * returns {
 *   UnstableConditions: ['Com Error #6', 'Com Error #10'],
 *   InputMissingVoltage: ['Com Error #2'],
 * }
 * 
 * const grouping = getCommissionResultConfig(commissionResult, 'ctaButtons');
 * returns {
 *   UnstableConditions: ['Troubleshoot', 'Reconfigure', 'TryAgain', 'ContactSupport'],
 *   InputMissingVoltage: ['Troubleshoot', 'Reconfigure', 'TryAgain', 'ContactSupport'],
 * }
 * @returns 
 */
export function getCommissionResultConfigValues(
  results: CommissionResult,
  configName: keyof (typeof COMMISSION_RESULT_CONFIGS)[keyof typeof COMMISSION_RESULT_CONFIGS] = 'failId',
): Partial<Record<ComponentErrorType, string[]>> {
  const grouping: Partial<Record<ComponentErrorType, string[]>> = {};

  Object.keys(results).forEach((terminal) => {
    const result = results[terminal as Terminal];
    if (result.verdict === COMMISSION_RESULT_VERDICT.FAIL) {
      const errorType = getComponentErrorType(result.reason as COMMISSION_RESULT_FAIL_REASON);
      const configEntry =
        COMMISSION_RESULT_CONFIGS[result.reason as COMMISSION_RESULT_FAIL_REASON];

      if (errorType) {
        if (!grouping[errorType]) {
          grouping[errorType] = [];
        }
        const bucket = grouping[errorType]!;
        const value = configEntry?.[configName];

        if (Array.isArray(value)) {
          for (const item of value) {
            if (!bucket.includes(item)) {
              bucket.push(item);
            }
          }
        } else if (value) {
          bucket.push(value);
        }
      }
    }
  });

  return grouping;
}

/**
 * Returns the solshare version and generation code from the serial number
 * @param serialNumber - The serial number
 * @returns The solshare version and generation code
 */
function __getSolshareVersionAndGenerationCodeFromSerialNumber(serialNumber: string | null = null) {
  if (serialNumber) {
    const solshareVersion = getSolshareVersion(serialNumber);
    const generation =
      solshareVersion === SOLSHARE_VERSION.Mk1 ? getMk1Generation(serialNumber) : null;
    return { solshareVersion, generation };
  }
  return null;
}

/**
 * Resolves error troubleshooting page path from a given serial number, fail reason, and country code.
 *
 * @param serialNumber - The serial number of the device
 * @param failReason - The reason for the error
 * @param countryCode - The country code of the device
 * @returns The error route parameters or null if no matching error page exists
 */
export function getErrorComponentPath(
  serialNumber: string | null = null,
  failReason: string | null = null,
  countryCode: string | null = null,
): string {
  if (serialNumber && failReason && countryCode) {
    const solshareVersionAndGeneration =
      __getSolshareVersionAndGenerationCodeFromSerialNumber(serialNumber);
    const solshareVersion = getComponentRouteSolshareVersionCode(
      solshareVersionAndGeneration?.solshareVersion,
    );
    const generation = getComponentRouteGenerationCode(solshareVersionAndGeneration?.generation);
    const country = getComponentRouteCountryCode(countryCode);
    const errorType = __isComponentErrorType(failReason)
      ? failReason
      : getComponentErrorType(failReason as COMMISSION_RESULT_FAIL_REASON);

    return __getPathToErrorTroubleshootingPage(
      solshareVersion as ComponentSolshareVersion,
      generation as ComponentGeneration,
      errorType as ComponentErrorType,
      country as ComponentCountryCode,
    );
  }

  return __getPathToErrorTroubleshootingPage();
}

/**
 * Resolves the component import path to the error troubleshooting page
 * @param solshareVersion - The component solshare version
 * @param errorType - The component error type
 * @param country - The component country code
 * @param generation - The component generation
 * @returns The component import path or 'DefaultErrorScreen.vue' if no matching error page exists
 */
function __getPathToErrorTroubleshootingPage(
  solshareVersion: ComponentSolshareVersion | null = null,
  generation: ComponentGeneration | null = null,
  errorType: ComponentErrorType | null = null,
  country: ComponentCountryCode | null = null,
  relativePathTo: string = './',
): string {
  // Path is relative to ErrorRouter.vue which is in the error-pages directory
  if (!errorType || !country || !solshareVersion)
    return `${relativePathTo}${DEFAULT_ERROR_COMPONENT}`;

  if (solshareVersion === 'Mk1' && MK2_ONLY_ERROR_TYPES.includes(errorType)) {
    return `${relativePathTo}${DEFAULT_ERROR_COMPONENT}`;
  }

  if (solshareVersion === 'Mk1') {
    return `${relativePathTo}${solshareVersion}/${generation}/${errorType}.${country}.vue`;
  }

  return `${relativePathTo}${solshareVersion}/${errorType}.${country}.vue`;
}
